/**
 * usePageDataCollector Hook
 *
 * Provides a global page data store that any page component can write to,
 * and the chatbot's useChatContext hook can read from. Uses a
 * window.__IMAGO_CHATBOT_CONTEXT__ object as the shared data bridge.
 *
 * Data structure:
 * {
 *   request:       { method, url, headers, auth_type, query_params, request_body },
 *   response:      { status_code, response_body, response_headers, response_time_ms, error_message },
 *   orchestration: { name, step_count, failed_steps, environment, dataset },
 *   mockServer:    { name, url, status, route_count, last_request_log },
 *   loadTest:      { p50, p90, p95, p99, throughput, error_rate, total_requests },
 *   environment:   { name },
 *   project:       { name },
 * }
 */

import { useCallback, useRef, useEffect } from 'react';

const CONTEXT_KEY = '__IMAGO_CHATBOT_CONTEXT__';

/** Ensure the global store exists */
function ensureStore() {
  if (!window[CONTEXT_KEY]) {
    window[CONTEXT_KEY] = {};
  }
  return window[CONTEXT_KEY];
}

/**
 * Truncate a string to a max length.
 * @param {string|null|undefined} value
 * @param {number} maxLen
 * @returns {string|null}
 */
function truncate(value, maxLen) {
  if (value == null) return null;
  const str = typeof value === 'string' ? value : JSON.stringify(value);
  return str.length > maxLen ? str.slice(0, maxLen) + '…' : str;
}

/**
 * Set page data into the global chatbot context store.
 * Merges the provided data with the existing store (shallow merge per top-level key).
 *
 * @param {Object} data - Partial page data to merge
 */
export function setPageData(data) {
  const store = ensureStore();
  Object.assign(store, data);
}

/**
 * Clear all page data from the global store.
 */
export function clearPageData() {
  window[CONTEXT_KEY] = {};
}

/**
 * Read the current page data snapshot from the global store.
 * @returns {Object}
 */
export function getPageData() {
  return ensureStore();
}

/**
 * Hook for page components to publish their context data.
 *
 * Returns a `setPageData` function that writes to the global store,
 * and automatically clears the data when the component unmounts.
 *
 * @returns {{ setPageData: Function, clearPageData: Function }}
 */
export function usePageDataCollector() {
  const keysSetRef = useRef(new Set());

  const set = useCallback((data) => {
    if (!data || typeof data !== 'object') return;

    // Apply truncation rules for known large fields
    const sanitized = { ...data };

    if (sanitized.request && sanitized.request.request_body) {
      sanitized.request = {
        ...sanitized.request,
        request_body: truncate(sanitized.request.request_body, 3000),
      };
    }

    if (sanitized.response && sanitized.response.response_body) {
      sanitized.response = {
        ...sanitized.response,
        response_body: truncate(sanitized.response.response_body, 2000),
      };
    }

    // Track which keys this component set so we can clean up on unmount
    Object.keys(sanitized).forEach((k) => keysSetRef.current.add(k));

    setPageData(sanitized);
  }, []);

  const clear = useCallback(() => {
    const store = ensureStore();
    keysSetRef.current.forEach((k) => {
      delete store[k];
    });
    keysSetRef.current.clear();
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      clear();
    };
  }, [clear]);

  return { setPageData: set, clearPageData: clear };
}

export default usePageDataCollector;
